import { useState, useEffect, useCallback } from 'react';
import { SortOrderItem } from '../components/shared/ui/SpendTable';
import { useViewerId } from './core';

import { logger } from '../utils/logger';

/**
 * useSortableTable - Reusable hook for table sorting logic
 * 
 * Encapsulates:
 * - Sort order state management
 * - localStorage persistence
 * - Sort storage key generation
 * - Reset functionality
 * 
 * Used by: CampaignSpend, AdminFeeTracker, BubbleSpendMatrix
 */
export function useSortableTable(
  prefix: string,
  defaultField: string,
  defaultDirection: 'asc' | 'desc' = 'asc'
) {
  const { viewerId } = useViewerId();
  
  // Generate unique storage key for this table
  const getSortStorageKey = useCallback((viewerId: string) => 
    `${prefix}-sort-${viewerId}`, 
    [prefix]
  );
  
  const DEFAULT_SORT_ORDER: SortOrderItem[] = [
    { field: defaultField, direction: defaultDirection }
  ];
  
  const sortStorageKey = getSortStorageKey(viewerId);
  
  // Initialize sort order from localStorage or default
  const [sortOrder, setSortOrder] = useState<SortOrderItem[]>(() => {
    const saved = localStorage.getItem(sortStorageKey);
    return saved ? JSON.parse(saved) : DEFAULT_SORT_ORDER;
  });
  
  // Persist sort order changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(sortStorageKey, JSON.stringify(sortOrder));
    } catch (error) {
      logger.warn('[useSortableTable] Failed to save sort order to localStorage:', error);
      // If quota exceeded, try to clear old entries
      if (error instanceof DOMException && error.code === 22) {
        try {
          // Clear old sort keys
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.includes('-sort')) {
              localStorage.removeItem(key);
            }
          }
          // Try again
          localStorage.setItem(sortStorageKey, JSON.stringify(sortOrder));
        } catch (retryError) {
          logger.error('[useSortableTable] Failed to clear localStorage and retry:', retryError);
        }
      }
    }
  }, [sortOrder, sortStorageKey]);
  
  // Reset sort order to default
  const handleResetSort = useCallback(() => {
    setSortOrder(DEFAULT_SORT_ORDER);
  }, [DEFAULT_SORT_ORDER]);
  
  return {
    sortOrder,
    setSortOrder,
    handleResetSort,
    DEFAULT_SORT_ORDER
  };
} 